import React from "react";
import {
  Box,
  Typography,
  ButtonBase,
} from "@mui/material";

/**
 * WeeklyRepeatOptions
 * -------------------
 * Independent component for weekly repeat configuration
 * - "Every N week(s)" with increment/decrement controls
 * - Weekday selection chips (Sun-Sat)
 * - Maintains at least one selected day
 */

const WEEKDAYS = [
  { key: 0, label: "Sun" },
  { key: 1, label: "Mon" },
  { key: 2, label: "Tue" },
  { key: 3, label: "Wed" },
  { key: 4, label: "Thu" },
  { key: 5, label: "Fri" },
  { key: 6, label: "Sat" },
];

export function WeeklyRepeatOptions({ value, onChange, coral }) {
  const textDark = "rgb(20, 20, 20)";
  const rowBorder = "1px solid rgba(0,0,0,0.12)";
  const softGrey = "rgba(0,0,0,0.06)";

  const interval = clampInt(value.interval ?? 1, 1, 365);
  const byWeekday = normalizeWeekdayList(value.byWeekday);

  function patch(p) {
    onChange({ ...value, ...p });
  }

  function decInterval() {
    patch({ interval: clampInt(interval - 1, 1, 365) });
  }

  function incInterval() {
    patch({ interval: clampInt(interval + 1, 1, 365) });
  }

  function toggleWeekday(dow) {
    const set = new Set(byWeekday);
    if (set.has(dow)) set.delete(dow);
    else set.add(dow);

    // Keep at least one day selected for weekly
    if (set.size === 0) return;

    patch({ byWeekday: Array.from(set).sort((a, b) => a - b) });
  }

  const everyLabel = `Every ${interval} ${interval === 1 ? "week" : "weeks"}`;

  return (
    <>
      {/* Every N weeks row */}
      <Box
        sx={{
          border: rowBorder,
          borderRadius: 999,
          px: 1.5,
          py: 0.75,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.25,
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 900, color: textDark }}>
          {renderEveryLabel(everyLabel, coral)}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1px 1fr",
            alignItems: "center",
            height: 34,
            borderRadius: 999,
            overflow: "hidden",
            border: "1px solid rgba(0,0,0,0.10)",
            background: softGrey,
            minWidth: 92,
          }}
        >
          <ButtonBase 
            onClick={decInterval} 
            sx={{ 
              height: "100%", 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center",
              "&:hover": { background: "rgba(0,0,0,0.05)" }
            }}
          >
            <Typography sx={{ fontSize: 18, fontWeight: 900, color: "rgba(0,0,0,0.50)" }}>−</Typography>
          </ButtonBase>

          <Box sx={{ width: 1, height: "70%", background: "rgba(0,0,0,0.15)" }} />

          <ButtonBase 
            onClick={incInterval} 
            sx={{ 
              height: "100%", 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center",
              "&:hover": { background: "rgba(0,0,0,0.05)" }
            }}
          >
            <Typography sx={{ fontSize: 18, fontWeight: 900, color: "rgba(0,0,0,0.50)" }}>+</Typography>
          </ButtonBase>
        </Box>
      </Box>

      {/* Weekday selection chips */}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.25 }}>
        {WEEKDAYS.map((d) => {
          const selected = byWeekday.includes(d.key);
          return (
            <ButtonBase
              key={d.key}
              onClick={() => toggleWeekday(d.key)}
              sx={{
                width: 44,
                height: 44,
                borderRadius: "999px",
                border: selected ? "1px solid rgba(244,159,153,0.35)" : "1px solid rgba(0,0,0,0.10)",
                background: selected ? "rgba(244,159,153,0.75)" : "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s ease",
                "&:hover": {
                  background: selected ? "rgba(244,159,153,0.85)" : "rgba(0,0,0,0.04)",
                },
              }}
              aria-pressed={selected}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 900,
                  color: selected ? "white" : "rgba(0,0,0,0.45)",
                }}
              >
                {d.label}
              </Typography>
            </ButtonBase>
          );
        })}
      </Box>
    </>
  );
}

/* ---------------- helpers ---------------- */

function clampInt(n, min, max) {
  return Math.max(min, Math.min(max, Math.floor(n)));
}

function normalizeWeekdayList(list) {
  if (!Array.isArray(list)) return [];
  const set = new Set();
  for (const x of list) {
    if (Number.isInteger(x) && x >= 0 && x <= 6) set.add(x);
  }
  return Array.from(set).sort((a, b) => a - b);
}

function renderEveryLabel(label, coral) {
  // label: "Every 2 weeks", "Every 1 week", etc.
  const m = label.match(/^Every\s+(\d+)\s+(.*)$/);
  if (!m) return label;
  return (
    <>
      Every{" "}
      <Box component="span" sx={{ color: coral }}>
        {m[1]}
      </Box>{" "}
      {m[2]}
    </>
  );
}
